import React, { useMemo } from 'react';

import { Popover, Space } from 'antd';

import { TEXT } from '@/shared/lib/constants/ui';
import { highlightText } from '@/shared/lib/text';
import type { BadgeItem } from '@/shared/types/badge';
import { Chip } from '@/shared/ui/Chip';
import type { IconName } from '@/shared/ui/Icon';
import { Icon } from '@/shared/ui/Icon';
import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './BadgeList.module.css';

type BadgeVariant = 'tag' | 'column';

type BadgeListProps = {
  items: BadgeItem[];
  onItemClick?: (fqn: string) => void;
  defaultColor?: string;
  isEllipsis?: boolean;
  iconName?: IconName;
  showIcon?: boolean;
  showTooltip?: boolean;
  existingValues?: Set<string>;
  maxVisibleCount?: number;
  disabledItems?: Set<string>;
  variant?: BadgeVariant;
  highlightQuery?: string;
  /** 빈 목록일 때만 표시. 미지정 시 기존과 같이 빈 `Space`만 렌더링 */
  emptyText?: string;
};

const getDisplayText = (item: BadgeItem, isEllipsis: boolean) => {
  if (!isEllipsis) return item.displayName || item.name;

  const displayText = item.displayName || item.name;
  const isTruncated = displayText.length > TEXT.MAX_TAG_LENGTH;
  const truncatedText = isTruncated ? `${displayText.slice(0, TEXT.MAX_TAG_LENGTH)}...` : displayText;

  return truncatedText;
};

const getChipLabel = (item: BadgeItem, isEllipsis: boolean, highlightQuery?: string): React.ReactNode => {
  const originalText = item.displayName || item.name;
  if (highlightQuery?.trim()) {
    return highlightText(originalText, highlightQuery, { className: 'highlight' });
  }
  return getDisplayText(item, isEllipsis);
};

export const BadgeList: React.FC<BadgeListProps> = ({
  isEllipsis = true,
  items,
  onItemClick,
  iconName = 'tag',
  showIcon = true,
  showTooltip = false,
  existingValues,
  maxVisibleCount,
  disabledItems,
  variant = 'tag',
  highlightQuery,
  emptyText,
}) => {
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      if (existingValues) {
        const aExisting = existingValues.has(a.tagFQN);
        const bExisting = existingValues.has(b.tagFQN);

        if (aExisting && !bExisting) return -1;
        if (!aExisting && bExisting) return 1;
      }

      const aDisplay = a.displayName || a.name;
      const bDisplay = b.displayName || b.name;
      return aDisplay.localeCompare(bDisplay, 'ko-KR');
    });
  }, [items, existingValues]);

  if (sortedItems.length === 0 && emptyText) {
    return <span className={styles.emptyText}>{emptyText}</span>;
  }

  const shouldShowPopover = maxVisibleCount !== undefined && sortedItems.length > maxVisibleCount;
  const visibleItems = shouldShowPopover ? sortedItems.slice(0, maxVisibleCount) : sortedItems;
  const remainingItems = shouldShowPopover ? sortedItems.slice(maxVisibleCount) : [];

  return (
    <Space size={[4, 6]} wrap>
      {visibleItems.map((item) => {
        const chipLabel = getChipLabel(item, isEllipsis, highlightQuery);
        const itemStyle = item.style?.color
          ? {
              backgroundColor: item.style.color,
              borderColor: item.style.color,
              ...(item.style.textColor && { color: item.style.textColor }),
            }
          : undefined;
        const isDisabled = disabledItems?.has(item.tagFQN) ?? false;

        const originalText = item.displayName || item.name;
        const isTruncated = isEllipsis && originalText.length > TEXT.MAX_TAG_LENGTH;
        const shouldShowTooltip = showTooltip && isTruncated;

        const chipElement = (
          <Chip
            key={item.tagFQN}
            icon={showIcon ? <Icon name={iconName} size={12} /> : undefined}
            label={chipLabel}
            disabled={isDisabled}
            {...(onItemClick &&
              !isDisabled && {
                onClick: () => onItemClick(item.tagFQN),
              })}
            style={itemStyle}
            className={variant === 'column' ? styles.columnVariant : undefined}
          />
        );

        if (shouldShowTooltip) {
          return (
            <Tooltip key={item.tagFQN} title={originalText} placement="top">
              <span className={styles.tooltipWrapper}>{chipElement}</span>
            </Tooltip>
          );
        }

        return chipElement;
      })}
      {shouldShowPopover && remainingItems.length > 0 && (
        <Popover
          content={
            <div className={styles.tagPopoverContent}>
              {remainingItems.map((item) => {
                const chipLabel = getChipLabel(item, isEllipsis, highlightQuery);
                const itemStyle = item.style?.color
                  ? {
                      backgroundColor: item.style.color,
                      borderColor: item.style.color,
                      ...(item.style.textColor && { color: item.style.textColor }),
                    }
                  : undefined;
                const isDisabled = disabledItems?.has(item.tagFQN) ?? false;

                const originalText = item.displayName || item.name;
                const isTruncated = isEllipsis && originalText.length > TEXT.MAX_TAG_LENGTH;
                const shouldShowTooltip = showTooltip && isTruncated;

                const chipElement = (
                  <Chip
                    key={item.tagFQN}
                    icon={showIcon ? <Icon name={iconName} size={12} /> : undefined}
                    label={chipLabel}
                    disabled={isDisabled}
                    {...(onItemClick &&
                      !isDisabled && {
                        onClick: () => onItemClick(item.tagFQN),
                      })}
                    style={itemStyle}
                    className={variant === 'column' ? styles.columnVariant : undefined}
                  />
                );

                if (shouldShowTooltip) {
                  return (
                    <Tooltip key={item.tagFQN} title={originalText} placement="top">
                      <span className={styles.tooltipWrapper}>{chipElement}</span>
                    </Tooltip>
                  );
                }

                return chipElement;
              })}
            </div>
          }
          trigger="hover"
          placement="topLeft">
          <span className={styles.tagCount}>+{remainingItems.length}</span>
        </Popover>
      )}
    </Space>
  );
};
